/**
 * ClientCareSettingsPage — admin-only configuration of per-company Client Care
 * cadences + notification preferences (the "3rd mockup screen").
 *
 * Route: /client-care-settings  (RequireRole ['super_admin', 'administrator'])
 * Backend: getClientCareConfig / updateClientCareConfig callables (both enforce
 * the same role gate server-side; this page is a thin editor over the config doc
 * at companies/{c}/client_care_config/default).
 *
 * Each cadence row carries a DATA-SOURCE tag (how its anchor is known) and a
 * `mode` that decides which timing input it shows. Modes map to the engine's
 * generation kinds in eventGeneration.ts:
 *   yearly   → fires on the anchor date each year (no offset input)
 *   interval → recurring every N days off last contact (intervalDays)
 *   lead     → N days BEFORE a future anchor (leadDays)
 *   after    → N days AFTER settlement, one-time (intervalDays as the offset)
 *   fixed    → anchor-free seasonal date (no offset input)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { CalendarClock, Save, Loader2, Bell, Mail, MessageSquare, Users, Trash2, UserPlus, Lock } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { functions, db } from '../../lib/firebase';
import { PageHeader } from '../../components/common/PageHeader';
import { HelpButton } from '../../components/help/HelpButton';
import {
  useClientCareConfig,
  type ClientCareConfig,
  type CadenceRule,
  type BrokerCadenceOverride,
  type ChannelPrefs,
  type EscalationRouting,
} from '../../hooks/useClientCareConfig';
import {
  MONTH_CADENCES,
  MONTH_CADENCE_DEFAULT,
  normalizeMonthCadences,
  durationOf,
  isInherited,
  formatDuration,
  formatApplies,
  type CadenceDuration,
} from './clientCareCadenceUtils';
import { useCrmConnection } from '../../hooks/useCrmConnection';
import {
  resolveCrmSourceStatus,
  resolveSourceMeta,
  type CrmSourceStatus,
  type DataSource,
} from './clientCareSourceMeta';
import {
  escalationScopeLabel,
  escalationSelectionIsInvalid,
} from './clientCareEscalationUtils';

/** A company broker (doc id + display name) — the per-broker override key space. */
interface BrokerOption {
  id: string; // brokerDocId (broker_… Firestore hash) — the cadencesByBroker key
  name: string;
}

type CadenceMode = 'yearly' | 'interval' | 'lead' | 'after' | 'fixed';

// Per-user email opt-out list (listClientCareRecipients / setClientCareEmailPreference).
interface CareRecipient {
  uid: string;
  role: string;
  email: string | null;
  displayName: string;
  status: string;
  clientCareEmailsOff: boolean;
  officeIds: string[];
  canSeeAllOffices: boolean;
  eligibleForClientCareEmail: boolean;
  eligibleForEscalation: boolean;
  escalationIneligibilityReason: string | null;
}

const ROLE_LABEL: Record<string, string> = {
  super_admin: 'Super admin',
  administrator: 'Administrator',
  office_admin: 'Office admin',
  broker: 'Broker',
  broker_assistant: 'Broker assistant',
  credit_analyst: 'Credit analyst',
};

const ESCALATION_MANAGER_ROLES = new Set(['super_admin', 'administrator', 'office_admin']);

const ESCALATION_REASON_LABEL: Record<string, string> = {
  inactive: 'Account is not active',
  invalid_role: 'Requires a management role',
  no_scope: 'No company or office scope',
  missing_email: 'No email address',
  emails_off: 'All Client Care emails are off',
  email_channel_off: 'Email is off for this management role above',
  wrong_company: 'Outside this company',
  not_found: 'User no longer exists',
};

interface CadenceDef {
  key: string;
  label: string;
  description: string;
  mode: CadenceMode;
  source: DataSource;
}

// Canonical recurring-event taxonomy — kept in sync with shared-schemas
// CLIENT_EVENT_TYPES and the generator's DESCRIPTORS.
const CADENCE_DEFS: CadenceDef[] = [
  // Relationship & milestones
  { key: 'settlement_anniversary', label: 'Settlement anniversary', description: 'Yearly check-in on each loan’s settlement date.', mode: 'yearly', source: 'auto' },
  { key: 'birthday', label: 'Birthday call', description: 'Reach out on the client’s birthday (if known).', mode: 'yearly', source: 'mycrm' },
  { key: 'periodic_review', label: 'Periodic relationship review', description: 'Recurring check-in; resets when a contact is logged.', mode: 'interval', source: 'auto' },
  { key: 'post_settlement', label: 'Settlement check-in', description: 'A one-time check-in a set time after each client settles. Set the company default, then override individual brokers.', mode: 'after', source: 'auto' },
  // Rate & product lifecycle
  { key: 'fixed_rate_expiry', label: 'Fixed-rate expiry', description: 'Before the fixed term rolls to variable — refinance/retention window.', mode: 'lead', source: 'mycrm' },
  { key: 'io_expiry', label: 'Interest-only term expiry', description: 'Before IO converts to P&I — payment-shock conversation.', mode: 'lead', source: 'mycrm' },
  { key: 'intro_rate_expiry', label: 'Intro / honeymoon rate expiry', description: 'Before a discounted intro period ends.', mode: 'lead', source: 'date' },
  { key: 'loan_maturity', label: 'Loan term maturity', description: 'Approaching end of loan term / bridging expiry.', mode: 'lead', source: 'mycrm' },
  // Refinance & equity
  { key: 'refinance_sweet_spot', label: 'Refinance sweet-spot', description: 'When the loan reaches a refinance-worthy age.', mode: 'after', source: 'auto' },
  // Compliance — Best Interests Duty
  { key: 'bid_annual', label: 'Annual suitability check-in (BID)', description: 'Documented yearly contact evidencing ongoing best interest.', mode: 'yearly', source: 'auto' },
  { key: 'pre_approval_expiry', label: 'Pre-approval expiry', description: 'Re-issue before the 90-day approval lapses.', mode: 'lead', source: 'mycrm' },
  // Re-engagement & seasonal
  { key: 'dormant', label: 'Dormant client', description: 'Re-engage clients with no recent contact.', mode: 'interval', source: 'auto' },
  { key: 'eofy', label: 'EOFY tax-time', description: 'Investors — statements, depreciation, structuring.', mode: 'fixed', source: 'auto' },
  { key: 'festive', label: 'Festive / holiday greeting', description: 'End-of-year touch.', mode: 'fixed', source: 'auto' },
];

const NOTIFY_ROLE_ROWS: Array<{ role: string; label: string; receives: string }> = [
  { role: 'broker', label: 'Broker', receives: 'Weekly digest · own clients' },
  { role: 'broker_assistant', label: 'Broker assistant', receives: 'Weekly digest · linked broker' },
  { role: 'credit_analyst', label: 'Credit analyst', receives: 'Weekly digest · linked broker(s)' },
  { role: 'office_admin', label: 'Office admin', receives: 'Weekly summary · their office' },
  { role: 'administrator', label: 'Administrator / Owner', receives: 'Weekly summary · all brokers' },
];

const DEFAULT_CADENCE: CadenceRule = { enabled: false, escalationDays: 7 };

function emptyConfig(): ClientCareConfig {
  return {
    cadences: {},
    cadencesByBroker: {},
    customEventTypes: [],
    notifications: { default: { email: true, chat: false }, byRole: {}, googleChatWebhookByOffice: {} },
    escalationRouting: { mode: 'role_based' },
    promptSendHour: 8,
  };
}

function Toggle({ checked, onChange, label, disabled, title }: { checked: boolean; onChange: (v: boolean) => void; label?: string; disabled?: boolean; title?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      title={title}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-blue-600' : 'bg-gray-600'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

function SourceTag({ source, crm }: { source: DataSource; crm: CrmSourceStatus }) {
  const m = resolveSourceMeta(source, crm);
  return (
    <span title={m.tip} className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${m.cls}`}>
      {m.label}
    </span>
  );
}

function parseIntOrUndefined(raw: string): number | undefined {
  const t = raw.trim();
  if (t === '') return undefined;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.floor(n);
}

/** Number input used for interval / lead / after offsets. */
function NumField({ label, value, min, placeholder, onChange }: { label: string; value?: number; min: number; placeholder: string; onChange: (v: number | undefined) => void }) {
  return (
    <label className="block">
      <span className="text-xs text-gray-400">{label}</span>
      <input
        type="number"
        min={min}
        value={value ?? ''}
        onChange={(e) => onChange(parseIntOrUndefined(e.target.value))}
        placeholder={placeholder}
        className="mt-1 w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-blue-500 focus:outline-none"
      />
    </label>
  );
}

/**
 * Cadence duration control: value + UNIT (weeks or months).
 *
 * Weeks exists because a 6-week post-settlement check-in cannot be expressed in
 * months, and the intent behind it ("at least one repayment has cleared and the
 * offset is linked") is lost when rounded to 1 or 2 months. Exactly one unit is
 * ever emitted; the caller rebuilds the rule so a saved cadence never carries
 * two units.
 *
 * When `includeDefault` is set (per-broker rows) an extra "Company default"
 * option represents an unset value that inherits the company setting.
 */
const DURATION_PRESETS: Array<{ key: string; label: string; d: CadenceDuration }> = [
  { key: 'w6', label: '6 weeks', d: { weeks: 6 } },
  { key: 'm3', label: '3 months', d: { months: 3 } },
  { key: 'm6', label: '6 months', d: { months: 6 } },
  { key: 'm12', label: '12 months', d: { months: 12 } },
];

const presetKeyOf = (d?: CadenceDuration): string | null =>
  d == null
    ? null
    : DURATION_PRESETS.find((p) => p.d.weeks === d.weeks && p.d.months === d.months)?.key ?? null;

const MAX_FOR_UNIT = { weeks: 520, months: 120 } as const;

function DurationField({
  value,
  onChange,
  includeDefault = false,
  ariaLabel,
}: {
  value?: CadenceDuration;
  onChange: (d: CadenceDuration | undefined) => void;
  includeDefault?: boolean;
  ariaLabel?: string;
}) {
  const preset = presetKeyOf(value);
  // A non-preset value is inherently "custom". `customEditing` keeps the number
  // input mounted after the user picks Custom… even while the value still
  // equals a preset (otherwise the mode would snap back and Custom… would be
  // unreachable).
  const [customEditing, setCustomEditing] = useState(false);
  const showCustom = customEditing || (value != null && preset == null);
  const mode = showCustom ? 'custom' : value == null ? (includeDefault ? 'default' : 'custom') : preset!;

  const unit: 'weeks' | 'months' = value?.weeks != null ? 'weeks' : 'months';
  const num = value?.weeks ?? value?.months ?? MONTH_CADENCE_DEFAULT;
  const emit = (n: number, u: 'weeks' | 'months') => {
    const clamped = Math.min(MAX_FOR_UNIT[u], Math.max(1, n));
    onChange(u === 'weeks' ? { weeks: clamped } : { months: clamped });
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <select
        aria-label={ariaLabel}
        value={mode}
        onChange={(e) => {
          const v = e.target.value;
          if (v === 'default') {
            setCustomEditing(false);
            onChange(undefined);
          } else if (v === 'custom') {
            setCustomEditing(true);
            if (value == null) onChange({ months: MONTH_CADENCE_DEFAULT }); // seed inherit -> editable
          } else {
            setCustomEditing(false);
            onChange(DURATION_PRESETS.find((p) => p.key === v)!.d);
          }
        }}
        className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-blue-500 focus:outline-none"
      >
        {includeDefault && <option value="default">Company default</option>}
        {DURATION_PRESETS.map((p) => (
          <option key={p.key} value={p.key}>{p.label}</option>
        ))}
        <option value="custom">Custom…</option>
      </select>
      {mode === 'custom' && (
        <span className="flex items-center gap-1.5">
          <input
            type="number"
            min={1}
            max={MAX_FOR_UNIT[unit]}
            value={num}
            // Clamp to the schema bounds — the backend hard-rejects the whole
            // save on an out-of-range value, so never let one into the draft.
            onChange={(e) => {
              const n = parseIntOrUndefined(e.target.value);
              if (n != null) emit(n, unit);
            }}
            aria-label={ariaLabel ? `${ariaLabel} value` : 'duration value'}
            className="w-20 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-blue-500 focus:outline-none"
          />
          <select
            value={unit}
            onChange={(e) => emit(num, e.target.value as 'weeks' | 'months')}
            aria-label={ariaLabel ? `${ariaLabel} unit` : 'duration unit'}
            className="bg-gray-900 border border-gray-700 rounded-lg px-2 py-2 text-sm text-white focus:border-blue-500 focus:outline-none"
          >
            <option value="weeks">weeks</option>
            <option value="months">months</option>
          </select>
        </span>
      )}
    </div>
  );
}

/**
 * Admin-managed per-broker timing overrides for one month-based cadence. Rows are
 * the brokers with an override; setting a row back to "Company default" (or the
 * trash) removes it. Governance: only super_admin/administrator reach this page,
 * so brokers can never edit their own timing.
 */
function PerBrokerTimingPanel({
  eventType,
  recurring,
  brokers,
  overrides,
  companyDuration,
  onSetDuration,
  onRemove,
  onAdd,
}: {
  eventType: string;
  recurring: boolean; // true = periodic review (every N); false = settlement check-in (N after)
  brokers: BrokerOption[];
  overrides: Record<string, BrokerCadenceOverride>;
  companyDuration: CadenceDuration;
  onSetDuration: (brokerId: string, eventType: string, d: CadenceDuration | undefined) => void;
  onRemove: (brokerId: string, eventType: string) => void;
  onAdd: (brokerId: string, eventType: string) => void;
}) {
  const overriddenIds = Object.keys(overrides);
  const nameOf = (id: string) => brokers.find((b) => b.id === id)?.name ?? id;
  const available = brokers
    .filter((b) => !overriddenIds.includes(b.id))
    .sort((a, b) => a.name.localeCompare(b.name));
  const applies = (d: CadenceDuration) => formatApplies(d, recurring);

  return (
    <div className="mt-4 border border-gray-700 rounded-lg bg-gray-900/40 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-700/60 flex items-center justify-between gap-3 flex-wrap">
        <span className="text-sm font-medium text-white flex items-center gap-2">
          <Users className="w-4 h-4 text-blue-400" />
          Per-broker timing
        </span>
        <span className="text-xs text-gray-400">Brokers not listed use the company default ({formatDuration(companyDuration)})</span>
      </div>
      <div className="px-4 py-2">
        {overriddenIds.length === 0 ? (
          <p className="text-xs text-gray-500 py-2">No per-broker overrides. Every broker uses the company default.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 text-[11px] uppercase tracking-wide">
                  <th className="py-2 pr-3 font-medium">Broker</th>
                  <th className="py-2 px-3 font-medium">{recurring ? 'Review frequency' : 'Check-in timing'}</th>
                  <th className="py-2 px-3 font-medium">Applies</th>
                  <th className="py-2 pl-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/60">
                {overriddenIds
                  .sort((a, b) => nameOf(a).localeCompare(nameOf(b)))
                  .map((id) => {
                    const ov = overrides[id];
                    const d = isInherited(ov) ? undefined : durationOf(ov);
                    return (
                      <tr key={id}>
                        <td className="py-2.5 pr-3 text-white">{nameOf(id)}</td>
                        <td className="py-2.5 px-3">
                          <DurationField includeDefault value={d} onChange={(v) => onSetDuration(id, eventType, v)} ariaLabel={`${nameOf(id)} timing`} />
                        </td>
                        <td className="py-2.5 px-3 text-gray-400 text-xs">
                          {d != null ? applies(d) : `default (${applies(companyDuration)})`}
                        </td>
                        <td className="py-2.5 pl-3 text-right">
                          <button
                            type="button"
                            onClick={() => onRemove(id, eventType)}
                            aria-label={`Remove ${nameOf(id)} override`}
                            className="text-gray-500 hover:text-red-400 p-1.5 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center justify-between gap-3 pt-3 mt-1 border-t border-gray-700/60 flex-wrap">
          <span className="text-[11px] text-gray-500 flex items-center gap-1.5">
            <Lock className="w-3 h-3" />
            Only admins and owners can change these. Brokers cannot edit their own timing.
          </span>
          {available.length > 0 && (
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-300">
              <UserPlus className="w-3.5 h-3.5 text-gray-400" />
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) onAdd(e.target.value, eventType);
                }}
                aria-label="Add broker override"
                className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:border-blue-500 focus:outline-none"
              >
                <option value="">Add broker override…</option>
                {available.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </label>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ClientCareSettingsPage() {
  const { userData, companyData } = useOutletContext<{
    userData: { companyId?: string; role?: string } | null;
    companyData: { featureFlags?: Record<string, unknown> } | null;
  }>();
  const companyId = userData?.companyId;

  // office_admin reaches this page READ-ONLY, and only while the tenant carries
  // the access-controls flag. The route guard admits the role; this pair is what
  // actually decides. `updateClientCareConfig` enforces the same rule server-side
  // (the nav and route gates are never the only control), so the worst case of a
  // gap here is a control that throws, not a write that lands.
  const isOfficeAdmin = userData?.role === 'office_admin';
  const officeAdminControlsEnabled =
    companyData?.featureFlags?.office_admin_access_controls === true;
  const readOnly = isOfficeAdmin;
  const officeAdminDenied = isOfficeAdmin && !officeAdminControlsEnabled;

  const { config, loading, saving, error, refetch, save } = useClientCareConfig(companyId);

  // Tenant CRM state, so the cadence rows that depend on CRM data say so
  // instead of naming an aggregator. getCrmConnectionStatus enforces
  // super_admin / administrator / marketing, which this route's guard
  // (super_admin + administrator) already satisfies. Loading and error both
  // resolve to 'unknown', which renders the neutral tag and leaves every
  // toggle enabled.
  const {
    connections: crmConnections,
    loading: crmLoading,
    error: crmError,
  } = useCrmConnection(companyId);
  const crmStatus = useMemo(
    () => resolveCrmSourceStatus({ connections: crmConnections, loading: crmLoading, error: crmError }),
    [crmConnections, crmLoading, crmError],
  );

  const [draft, setDraft] = useState<ClientCareConfig>(emptyConfig());

  useEffect(() => {
    if (config) setDraft(normalizeMonthCadences({ ...emptyConfig(), ...config }));
  }, [config]);

  // Company brokers (doc id + name) — the per-broker override key space. Read
  // straight from Firestore (same source Team Management uses); the doc id is the
  // `broker_…` Firestore hash the roster materializer resolves each client to.
  const [brokers, setBrokers] = useState<BrokerOption[]>([]);
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, `companies/${companyId}/brokers`));
        const list = snap.docs
          .map((d) => ({
            id: d.id,
            name: (d.data().currentName as string) || d.id,
            mergedInto: d.data().mergedInto as string | undefined,
            status: d.data().status as string | undefined,
          }))
          .filter((b) => !b.mergedInto && b.status !== 'archived')
          .map((b): BrokerOption => ({ id: b.id, name: b.name }))
          .sort((a, b) => a.name.localeCompare(b.name));
        if (!cancelled) setBrokers(list);
      } catch (e) {
        console.error('[ClientCareSettings] broker load failed:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // ---- Per-user email recipients (opt-out list) ----
  const [recipients, setRecipients] = useState<CareRecipient[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [togglingUid, setTogglingUid] = useState<string | null>(null);

  const loadRecipients = useCallback(async () => {
    if (!companyId) return;
    setRecipientsLoading(true);
    try {
      const call = httpsCallable<{ companyId: string }, { recipients: CareRecipient[] }>(
        functions,
        'listClientCareRecipients',
      );
      const res = await call({ companyId });
      setRecipients(res.data.recipients ?? []);
    } catch (err) {
      console.error('[ClientCareSettings] failed to load recipients', err);
      toast.error('Could not load email recipients');
    } finally {
      setRecipientsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void loadRecipients();
  }, [loadRecipients]);

  // Toggle a single user's "receives Client Care email" switch (off = silenced).
  const setRecipientEnabled = useCallback(
    async (uid: string, enabled: boolean) => {
      if (!companyId) return;
      const off = !enabled;
      setTogglingUid(uid);
      const withPreference = (recipient: CareRecipient, nextOff: boolean): CareRecipient => {
        if (recipient.uid !== uid) return recipient;
        const reason = nextOff
          ? recipient.escalationIneligibilityReason ?? 'emails_off'
          : recipient.escalationIneligibilityReason === 'emails_off'
            ? null
            : recipient.escalationIneligibilityReason;
        return {
          ...recipient,
          clientCareEmailsOff: nextOff,
          eligibleForClientCareEmail: !nextOff && !!recipient.email && recipient.status === 'active',
          eligibleForEscalation: reason === null,
          escalationIneligibilityReason: reason,
        };
      };
      // Optimistic update; revert on failure.
      setRecipients((prev) => prev.map((recipient) => withPreference(recipient, off)));
      try {
        const call = httpsCallable<{ companyId: string; targetUid: string; off: boolean }, { ok: boolean }>(
          functions,
          'setClientCareEmailPreference',
        );
        await call({ companyId, targetUid: uid, off });
        toast.success(enabled ? 'Emails enabled' : 'Emails turned off');
      } catch (err) {
        console.error('[ClientCareSettings] failed to set recipient preference', err);
        setRecipients((prev) => prev.map((recipient) => withPreference(recipient, !off)));
        toast.error('Could not update — try again');
      } finally {
        setTogglingUid(null);
      }
    },
    [companyId],
  );

  const escalationCandidates = useMemo(
    () => recipients
      .filter((recipient) => ESCALATION_MANAGER_ROLES.has(recipient.role))
      .map((recipient) => {
        const notifyRole = recipient.role === 'super_admin' ? 'administrator' : recipient.role;
        const emailEnabled =
          (draft.notifications.byRole?.[notifyRole] ?? draft.notifications.default).email;
        return recipient.eligibleForEscalation && !emailEnabled
          ? {
              ...recipient,
              eligibleForEscalation: false,
              escalationIneligibilityReason: 'email_channel_off',
            }
          : recipient;
      }),
    [draft.notifications, recipients],
  );
  const selectedEscalationUids = useMemo(
    () =>
      draft.escalationRouting.mode === 'selected_recipients'
        ? draft.escalationRouting.recipientUids
        : [],
    [draft.escalationRouting],
  );
  const selectedEscalationSet = useMemo(
    () => new Set(selectedEscalationUids),
    [selectedEscalationUids],
  );
  const escalationCandidatesByUid = useMemo(
    () => new Map(escalationCandidates.map((recipient) => [recipient.uid, recipient])),
    [escalationCandidates],
  );
  const unavailableSelectedUids = useMemo(
    () => selectedEscalationUids.filter((uid) => !escalationCandidatesByUid.has(uid)),
    [escalationCandidatesByUid, selectedEscalationUids],
  );
  const escalationSelectionInvalid = escalationSelectionIsInvalid(
    draft.escalationRouting,
    escalationCandidates,
  );

  const setEscalationMode = useCallback((mode: EscalationRouting['mode']) => {
    setDraft((prev) => ({
      ...prev,
      escalationRouting:
        mode === 'role_based'
          ? { mode: 'role_based' }
          : {
              mode: 'selected_recipients',
              recipientUids:
                prev.escalationRouting.mode === 'selected_recipients'
                  ? prev.escalationRouting.recipientUids
                  : [],
            },
    }));
  }, []);

  const setEscalationRecipient = useCallback((uid: string, selected: boolean) => {
    setDraft((prev) => {
      const existing =
        prev.escalationRouting.mode === 'selected_recipients'
          ? prev.escalationRouting.recipientUids
          : [];
      const next = selected
        ? [...new Set([...existing, uid])].sort((a, b) => a.localeCompare(b))
        : existing.filter((candidateUid) => candidateUid !== uid);
      return {
        ...prev,
        escalationRouting: { mode: 'selected_recipients', recipientUids: next },
      };
    });
  }, []);

  const cadenceFor = useCallback(
    (key: string): CadenceRule => draft.cadences?.[key] ?? DEFAULT_CADENCE,
    [draft.cadences],
  );

  const setCadence = useCallback((key: string, patch: Partial<CadenceRule>) => {
    setDraft((prev) => {
      const current = prev.cadences?.[key] ?? DEFAULT_CADENCE;
      return { ...prev, cadences: { ...prev.cadences, [key]: { ...current, ...patch } } };
    });
  }, []);

  const companyDurationFor = useCallback(
    (eventType: string): CadenceDuration => durationOf(draft.cadences?.[eventType]),
    [draft.cadences],
  );

  /**
   * Set the company-level duration for a month/week cadence.
   *
   * Rebuilds the rule WITHOUT the other unit keys rather than spreading a patch
   * containing `undefined`: the Firebase callable serialiser encodes a present
   * `undefined` as `null`, and the server schema's `.optional()` rejects null.
   * Deleting the keys keeps exactly one unit on the saved document.
   */
  const setCadenceDuration = useCallback((key: string, d: CadenceDuration | undefined) => {
    setDraft((prev) => {
      const current = prev.cadences?.[key] ?? DEFAULT_CADENCE;
      const { intervalWeeks: _w, intervalMonths: _m, intervalDays: _d, ...rest } = current;
      const next =
        d?.weeks != null
          ? { ...rest, intervalWeeks: d.weeks }
          : { ...rest, intervalMonths: d?.months ?? MONTH_CADENCE_DEFAULT };
      return { ...prev, cadences: { ...prev.cadences, [key]: next } };
    });
  }, []);

  // Per-broker overrides for one event type: brokerDocId -> override.
  const overridesForEvent = useCallback(
    (eventType: string): Record<string, BrokerCadenceOverride> => {
      const out: Record<string, BrokerCadenceOverride> = {};
      for (const [brokerId, byType] of Object.entries(draft.cadencesByBroker ?? {})) {
        if (byType[eventType]) out[brokerId] = byType[eventType];
      }
      return out;
    },
    [draft.cadencesByBroker],
  );

  // Set a duration, or clear it (undefined => inherit the company default), on a
  // broker's override for an event type; prunes empty override / broker entries
  // so an "inherit default" selection leaves no residue in cadencesByBroker.
  // Exactly one unit key survives, and cleared keys are DELETED rather than set
  // to undefined (which the callable would send as null and the schema reject).
  const setBrokerDuration = useCallback(
    (brokerId: string, eventType: string, d: CadenceDuration | undefined) => {
    setDraft((prev) => {
      const byBroker = { ...(prev.cadencesByBroker ?? {}) };
      const forBroker = { ...(byBroker[brokerId] ?? {}) };
      const { intervalMonths: _dm, intervalWeeks: _dw, ...rest } = forBroker[eventType] ?? {};
      if (d == null) {
        if (Object.keys(rest).length === 0) delete forBroker[eventType];
        else forBroker[eventType] = rest;
      } else if (d.weeks != null) {
        forBroker[eventType] = { ...rest, intervalWeeks: d.weeks };
      } else {
        forBroker[eventType] = { ...rest, intervalMonths: d.months ?? MONTH_CADENCE_DEFAULT };
      }
      if (Object.keys(forBroker).length === 0) delete byBroker[brokerId];
      else byBroker[brokerId] = forBroker;
      return { ...prev, cadencesByBroker: byBroker };
    });
  },
    [],
  );

  const removeBrokerOverride = useCallback((brokerId: string, eventType: string) => {
    setDraft((prev) => {
      const byBroker = { ...(prev.cadencesByBroker ?? {}) };
      const forBroker = { ...(byBroker[brokerId] ?? {}) };
      delete forBroker[eventType];
      if (Object.keys(forBroker).length === 0) delete byBroker[brokerId];
      else byBroker[brokerId] = forBroker;
      return { ...prev, cadencesByBroker: byBroker };
    });
  }, []);

  // "Add broker override": seed the row at the company default so it appears and
  // is immediately editable (admin then dials it to 3/6/12/custom).
  const addBrokerOverride = useCallback(
    (brokerId: string, eventType: string) => setBrokerDuration(brokerId, eventType, companyDurationFor(eventType)),
    [setBrokerDuration, companyDurationFor],
  );

  const setDefaultChannel = useCallback((channel: 'email' | 'chat', value: boolean) => {
    setDraft((prev) => ({
      ...prev,
      notifications: { ...prev.notifications, default: { ...prev.notifications.default, [channel]: value } },
    }));
  }, []);

  // Per-role channel override. Falls back to the company default when unset.
  const channelsForRole = useCallback(
    (role: string): ChannelPrefs => draft.notifications.byRole?.[role] ?? draft.notifications.default,
    [draft.notifications],
  );

  const setRoleChannel = useCallback((role: string, channel: 'email' | 'chat', value: boolean) => {
    setDraft((prev) => {
      const base = prev.notifications.byRole?.[role] ?? prev.notifications.default;
      return {
        ...prev,
        notifications: {
          ...prev.notifications,
          byRole: { ...prev.notifications.byRole, [role]: { ...base, [channel]: value } },
        },
      };
    });
  }, []);

  const setPromptHour = useCallback((hour: number) => {
    setDraft((prev) => ({ ...prev, promptSendHour: hour }));
  }, []);

  const enabledCount = useMemo(() => CADENCE_DEFS.filter((d) => cadenceFor(d.key).enabled).length, [cadenceFor]);

  const onSave = useCallback(async () => {
    if (escalationSelectionInvalid) {
      toast.error('Choose at least one active, eligible escalation recipient');
      return;
    }
    // Normalize BEFORE sending so an enabled month cadence always persists an
    // explicit intervalMonths (fixes the display-only "6 months" that never
    // committed on a fresh toggle-on).
    const { updatedAt: _a, updatedBy: _b, ...payload } = normalizeMonthCadences(draft);
    const ok = await save(payload as ClientCareConfig);
    if (ok) toast.success('Client Care settings saved');
    else toast.error('Could not save settings');
  }, [draft, escalationSelectionInvalid, save]);

  if (!companyId) {
    return (
      <div className="space-y-6">
        <PageHeader icon={CalendarClock} title="Client Care Settings" subtitle="Cadences and notification preferences" />
        <div className="min-h-[50vh] flex items-center justify-center text-gray-400">No company in scope.</div>
      </div>
    );
  }

  if (officeAdminDenied) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="min-h-[50vh] flex items-center justify-center text-gray-400">
          Client Care Settings is not available for this account.
        </div>
      </div>
    );
  }

  const saveBtn = readOnly ? (
    <span className="inline-flex items-center gap-2 rounded-lg border border-gray-600 px-3 py-2 text-xs font-semibold text-gray-400">
      <Lock className="w-3.5 h-3.5" aria-hidden="true" />
      View only
    </span>
  ) : (
    <button
      type="button"
      onClick={onSave}
      // `!config` guards the destructive whole-map save: after a FAILED load the
      // draft is still emptyConfig(), and saving it would wipe every per-broker
      // override (the backend hard-replaces cadencesByBroker when the key is
      // present, and the UI always sends it whole).
      disabled={saving || loading || !config || escalationSelectionInvalid}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
      <span>{saving ? 'Saving…' : 'Save changes'}</span>
    </button>
  );

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <PageHeader
        icon={CalendarClock}
        title="Client Care Settings"
        subtitle="Configure how and when Client Care reaches out — cadences, escalation, and notifications."
        badge="Admin"
        onRefresh={refetch}
        isRefreshing={loading}
        actions={<><HelpButton pageKey="clientCareSettings" />{saveBtn}</>}
      />

      {readOnly && (
        <div className="flex items-start gap-2 rounded-xl border border-blue-500/30 bg-blue-500/10 px-3.5 py-3 text-xs text-blue-200">
          <Lock className="mt-0.5 w-4 h-4 flex-none" aria-hidden="true" />
          <span>
            You can review the Client Care configuration for your offices. Changes are made by
            a company administrator.
          </span>
        </div>
      )}

      {/* `display: contents` keeps the grid/space-y layout byte-identical while
          the fieldset's `disabled` natively disables every control inside for a
          read-only viewer (mouse AND keyboard) - a toggle that silently does
          nothing is worse than one that is visibly unavailable. */}
      <fieldset disabled={readOnly} className="contents">

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-lg px-4 py-3 text-sm">{error}</div>
      )}

      {/* Cadences */}
      <section className="bg-gray-800 border border-gray-700 rounded-lg">
        <div className="px-5 py-4 border-b border-gray-700 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-blue-400" />
              Care Cadences
            </h3>
            <p className="text-gray-400 text-sm mt-0.5">
              Choose which moments trigger an outreach prompt, and how long before escalation.
            </p>
            {/* Data-source legend */}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className="text-[11px] text-gray-500 uppercase tracking-wide font-bold">Data source</span>
              {(['auto', 'date', 'mycrm'] as DataSource[]).map((s) => (
                <span key={s} className="inline-flex items-center gap-1.5">
                  <SourceTag source={s} crm={crmStatus} />
                  <span className="text-[11px] text-gray-500">{resolveSourceMeta(s, crmStatus).tip}</span>
                </span>
              ))}
            </div>
          </div>
          <span className="text-xs text-gray-400 whitespace-nowrap">{enabledCount} enabled</span>
        </div>

        <div className="divide-y divide-gray-700">
          {CADENCE_DEFS.map((def) => {
            const rule = cadenceFor(def.key);
            const sourceMeta = resolveSourceMeta(def.source, crmStatus);
            return (
              <div key={def.key} className="px-5 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-medium">{def.label}</span>
                      <SourceTag source={def.source} crm={crmStatus} />
                    </div>
                    <div className="text-gray-400 text-sm mt-0.5">{def.description}</div>
                  </div>
                  <Toggle
                    checked={!!rule.enabled}
                    onChange={(v) => setCadence(def.key, { enabled: v })}
                    label={`Enable ${def.label}`}
                    // Disable only what would turn a CRM cadence ON without a CRM.
                    // An ALREADY-ENABLED cadence keeps its toggle, or the admin is
                    // trapped: Loan Market Double Bay has four enabled mycrm cadences
                    // and no crm_connections doc, so a flat `disabled` would freeze
                    // them ON. Preserving the value but removing the control is the
                    // mirror image of the defect the visible-row decision avoided.
                    disabled={sourceMeta.disabled && !rule.enabled}
                    title={sourceMeta.disabled && !rule.enabled ? sourceMeta.tip : undefined}
                  />
                </div>

                {rule.enabled && (
                  <>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {MONTH_CADENCES.has(def.key) ? (
                        <div>
                          <span className="text-xs text-gray-400">
                            {def.key === 'periodic_review' ? 'Company default frequency' : 'Company default timing'}
                          </span>
                          <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <DurationField
                              value={durationOf(rule)}
                              onChange={(v) => setCadenceDuration(def.key, v)}
                              ariaLabel={`${def.label} company default`}
                            />
                            {def.key !== 'periodic_review' && <span className="text-xs text-gray-500">after settlement</span>}
                          </div>
                        </div>
                      ) : def.mode === 'interval' ? (
                        <NumField label="Every (days)" value={rule.intervalDays} min={1} placeholder="e.g. 180" onChange={(v) => setCadence(def.key, { intervalDays: v })} />
                      ) : def.mode === 'lead' ? (
                        <NumField label="Lead time (days before)" value={rule.leadDays} min={0} placeholder="e.g. 60" onChange={(v) => setCadence(def.key, { leadDays: v })} />
                      ) : def.mode === 'after' ? (
                        <NumField label="Days after settlement" value={rule.intervalDays} min={1} placeholder={def.key === 'refinance_sweet_spot' ? 'e.g. 730' : 'e.g. 30'} onChange={(v) => setCadence(def.key, { intervalDays: v })} />
                      ) : null}
                      {/* yearly + fixed have no offset input; they fire on the date itself */}

                      <NumField label="Escalate after (days)" value={rule.escalationDays} min={0} placeholder="7" onChange={(v) => setCadence(def.key, { escalationDays: v ?? 7 })} />
                    </div>
                    {MONTH_CADENCES.has(def.key) && (
                      <PerBrokerTimingPanel
                        eventType={def.key}
                        recurring={def.key === 'periodic_review'}
                        brokers={brokers}
                        overrides={overridesForEvent(def.key)}
                        companyDuration={durationOf(rule)}
                        onSetDuration={setBrokerDuration}
                        onRemove={removeBrokerOverride}
                        onAdd={addBrokerOverride}
                      />
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Notifications */}
      <section className="bg-gray-800 border border-gray-700 rounded-lg">
        <div className="px-5 py-4 border-b border-gray-700">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Bell className="w-4 h-4 text-blue-400" />
            Notifications
          </h3>
          <p className="text-gray-400 text-sm mt-0.5">
            Set a company-wide default, then customise per role. Email is per-person; Google Chat posts to a team space via webhook.
          </p>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Company default */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <Mail className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium">Email (company default)</span>
            </div>
            <Toggle checked={!!draft.notifications.default.email} onChange={(v) => setDefaultChannel('email', v)} label="Default email" />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <MessageSquare className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium">Google Chat (company default)</span>
            </div>
            <Toggle checked={!!draft.notifications.default.chat} onChange={(v) => setDefaultChannel('chat', v)} label="Default chat" />
          </div>

          <label className="block max-w-xs">
            <span className="text-xs text-gray-400">Daily prompt send hour (Brisbane)</span>
            <select
              value={draft.promptSendHour}
              onChange={(e) => setPromptHour(Number(e.target.value))}
              className="mt-1 w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-blue-500 focus:outline-none"
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
              ))}
            </select>
          </label>

          {/* Per-role matrix */}
          <div className="pt-2">
            <h4 className="text-[11px] uppercase tracking-wide text-gray-500 font-bold mb-2">Customise per role</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400">
                    <th className="py-2 pr-4 font-medium">Role</th>
                    <th className="py-2 px-3 font-medium text-center">Email</th>
                    <th className="py-2 px-3 font-medium text-center">Google Chat</th>
                    <th className="py-2 pl-3 font-medium">Receives</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/60">
                  {NOTIFY_ROLE_ROWS.map((r) => {
                    const ch = channelsForRole(r.role);
                    return (
                      <tr key={r.role}>
                        <td className="py-2.5 pr-4 text-white">{r.label}</td>
                        <td className="py-2.5 px-3 text-center">
                          <div className="flex justify-center">
                            <Toggle checked={!!ch.email} onChange={(v) => setRoleChannel(r.role, 'email', v)} label={`${r.label} email`} />
                          </div>
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <div className="flex justify-center">
                            <Toggle checked={!!ch.chat} onChange={(v) => setRoleChannel(r.role, 'chat', v)} label={`${r.label} chat`} />
                          </div>
                        </td>
                        <td className="py-2.5 pl-3 text-gray-400">{r.receives}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-gray-500 mt-2">
              Google Chat posts to a shared team space (not an individual DM) — best for office escalation channels. Per-person Chat can come later; email already reaches each person directly.
            </p>
          </div>
        </div>
      </section>

      {/* Escalation recipients — tenant-owned allow-list for the weekly manager summary. */}
      <section className="bg-gray-800 border border-gray-700 rounded-lg">
        <div className="px-5 py-4 border-b border-gray-700">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Bell className="w-4 h-4 text-blue-400" />
            Escalation recipients
          </h3>
          <p className="text-gray-400 text-sm mt-0.5">
            Choose who receives the Monday summary when brokers have not completed their calls. Broker call digests are configured separately and are not changed here.
          </p>
        </div>

        <div className="px-5 py-4 space-y-4">
          <label className="block max-w-md">
            <span className="text-xs text-gray-400">Recipient policy</span>
            <select
              value={draft.escalationRouting.mode}
              onChange={(event) => setEscalationMode(event.target.value as EscalationRouting['mode'])}
              className="mt-1 w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-blue-500 focus:outline-none"
            >
              <option value="role_based">Automatic by management role (legacy)</option>
              <option value="selected_recipients">Selected people only</option>
            </select>
          </label>

          {draft.escalationRouting.mode === 'role_based' ? (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
              Covering office managers and administrators are selected automatically, and every active super admin is included. Choose “Selected people only” to prevent new owners being silently subscribed.
            </div>
          ) : recipientsLoading ? (
            <div className="text-gray-400 text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading eligible managers…
            </div>
          ) : (
            <div className="space-y-3">
              <div className="divide-y divide-gray-700 rounded-lg border border-gray-700">
                {escalationCandidates.length === 0 ? (
                  <div className="px-4 py-5 text-sm text-gray-400">No management accounts are available.</div>
                ) : (
                  escalationCandidates.map((recipient) => {
                    const selected = selectedEscalationSet.has(recipient.uid);
                    const canToggle = recipient.eligibleForEscalation || selected;
                    const reason = recipient.escalationIneligibilityReason
                      ? ESCALATION_REASON_LABEL[recipient.escalationIneligibilityReason] ?? recipient.escalationIneligibilityReason
                      : null;
                    return (
                      <div key={recipient.uid} className="px-4 py-3 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white font-medium truncate">{recipient.displayName}</span>
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border border-gray-600 text-gray-300">
                              {ROLE_LABEL[recipient.role] ?? recipient.role}
                            </span>
                            <span className="text-[10px] font-semibold text-blue-300">{escalationScopeLabel(recipient)}</span>
                          </div>
                          <div className="text-gray-500 text-xs mt-0.5 truncate">{recipient.email ?? 'No email address'}</div>
                          {reason && <div className="text-amber-300 text-xs mt-1">{reason}</div>}
                        </div>
                        <Toggle
                          checked={selected}
                          onChange={(value) => setEscalationRecipient(recipient.uid, value)}
                          disabled={!canToggle}
                          label={`Escalation summaries for ${recipient.displayName}`}
                        />
                      </div>
                    );
                  })
                )}
                {unavailableSelectedUids.map((uid) => (
                  <div key={uid} className="px-4 py-3 flex items-center justify-between gap-4 bg-red-500/5">
                    <div>
                      <div className="text-white text-sm font-medium">Unavailable selected account</div>
                      <div className="text-red-300 text-xs mt-1">This person was removed or no longer has a management role.</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEscalationRecipient(uid, false)}
                      className="text-xs font-medium text-red-300 hover:text-red-200"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>

              {escalationSelectionInvalid && (
                <p className="text-sm text-amber-300">
                  Select at least one active manager with email access and company or office coverage before saving.
                </p>
              )}
              <p className="text-xs text-gray-500">
                Selected administrators and office admins receive only overdue calls within their existing office access. Super admins cover the whole company. If coverage later disappears, Valutten sends to nobody else and raises an operational alert.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Email recipients — per-user opt-out (saves immediately, independent of the Save button) */}
      <section className="bg-gray-800 border border-gray-700 rounded-lg">
        <div className="px-5 py-4 border-b border-gray-700">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-400" />
            All Client Care email access
          </h3>
          <p className="text-gray-400 text-sm mt-0.5">
            This is the master email switch for each person. Turning someone off silences both their own call digest and any escalation summary, regardless of the selection above. Changes save immediately.
          </p>
        </div>

        <div className="divide-y divide-gray-700">
          {recipientsLoading ? (
            <div className="px-5 py-6 text-gray-400 text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading recipients…
            </div>
          ) : recipients.length === 0 ? (
            <div className="px-5 py-6 text-gray-400 text-sm">No email recipients found for this company.</div>
          ) : (
            recipients.map((r) => {
              const enabled = !r.clientCareEmailsOff;
              const canToggle = !!r.email && r.status === 'active';
              return (
                <div key={r.uid} className="px-5 py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-medium truncate">{r.displayName}</span>
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border border-gray-600 text-gray-300">
                        {ROLE_LABEL[r.role] ?? r.role}
                      </span>
                      {!enabled && <span className="text-[10px] font-semibold text-amber-300">Emails off</span>}
                    </div>
                    <div className="text-gray-500 text-xs mt-0.5 truncate">{r.email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {togglingUid === r.uid && <Loader2 className="w-4 h-4 animate-spin text-gray-500" />}
                    <Toggle
                      checked={enabled}
                      onChange={(v) => setRecipientEnabled(r.uid, v)}
                      disabled={!canToggle}
                      label={`Client Care emails for ${r.displayName}`}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      </fieldset>

      <div className="flex justify-end">{saveBtn}</div>
    </div>
  );
}
